import type { ViewerCamera } from '@geosoft/viewer-sdk';
import { Chip, Stack } from '@/design';
import type { MapStateFlags } from './mapModel';

interface MapStatusBarProps {
  camera: ViewerCamera | null;
  crs: string;
  flags: MapStateFlags;
}

function coordinateLabel(camera: ViewerCamera | null): string {
  if (!camera) return 'Center unavailable';
  const [lon, lat] = camera.lonLat;
  return `${lon.toFixed(5)}, ${lat.toFixed(5)}`;
}

function scaleLabel(camera: ViewerCamera | null): string {
  if (!camera || camera.zoom == null) return 'Scale unavailable';
  return `Zoom ${camera.zoom.toFixed(2)}`;
}

function tileStatus(flags: MapStateFlags): { icon: 'sync_problem' | 'cloud_off' | 'check_circle'; label: string; variant: 'warning' | 'error' | 'success' } {
  if (flags.offline) return { icon: 'cloud_off', label: 'Tiles offline', variant: 'error' };
  if (flags.stale || flags.partialLoading) {
    return { icon: 'sync_problem', label: 'Tiles degraded', variant: 'warning' };
  }
  return { icon: 'check_circle', label: 'Tiles healthy', variant: 'success' };
}

export function MapStatusBar({ camera, crs, flags }: MapStatusBarProps) {
  const tile = tileStatus(flags);
  return (
    <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
      <Chip
        icon="pin_drop"
        label={coordinateLabel(camera)}
        variant="neutral"
        aria-label={`Coordinates ${coordinateLabel(camera)}`}
      />
      <Chip icon="straighten" label={scaleLabel(camera)} variant="neutral" aria-label={scaleLabel(camera)} />
      <Chip icon="language" label={crs} variant="neutral" aria-label={`Coordinate reference system ${crs}`} />
      <Chip icon={tile.icon} label={tile.label} variant={tile.variant} aria-label={tile.label} />
    </Stack>
  );
}
